import type { TabProps, TabsProps } from '@mui/material';
import { Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';

// Size configurations
const sizeConfigs = {
  small: {
    height: 36,
    fontSize: 13,
    padding: '6px 16px',
    borderRadius: 6,
  },
  medium: {
    height: 42,
    fontSize: 14,
    padding: '8px 20px',
    borderRadius: 8,
  },
  large: {
    height: 48,
    fontSize: 16,
    padding: '10px 24px',
    borderRadius: 10,
  },
};

interface StyledTabsProps extends TabsProps {
  size?: 'small' | 'medium' | 'large';
}

interface StyledTabProps extends TabProps {
  size?: 'small' | 'medium' | 'large';
}

export const StyledTabs: React.ComponentType<StyledTabsProps> = styled(Tabs)<StyledTabsProps>(({
  size = 'medium',
}) => {
  const config = sizeConfigs[size];

  return {
    minHeight: config.height,
    height: config.height,
    backgroundColor: 'rgba(230, 225, 255, 0.2)', // Very light purple background
    borderRadius: config.borderRadius,
    padding: 0,
    position: 'relative',
    display: 'inline-flex',
    width: 'auto',
    outline: `1px solid rgba(0, 13, 77, 0.12)`, // Container border
    outlineOffset: -1,
    overflow: 'visible', // Allow indicator to overlap container

    // Style the indicator to be a full-height sliding background
    '& .MuiTabs-indicator': {
      height: config.height,
      top: 0, // Overlap the container border
      borderRadius: config.borderRadius,
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0, 13, 77, 0.45)', // Lighter border for the indicator
      zIndex: 1,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 2px 4px -2px rgba(0, 0, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.04)',
    },

    // Remove the default scrollbar buttons
    '& .MuiTabs-scrollButtons': {
      display: 'none',
    },

    // Allow scroller to show overflow
    '& .MuiTabs-scroller': {
      overflow: 'visible !important',
    },

    // Ensure flexContainer fills the height
    '& .MuiTabs-flexContainer': {
      height: '100%',
      position: 'relative',
      zIndex: 2, // Keep tabs above the indicator
    },
  };
});

export const StyledTab: React.ComponentType<StyledTabProps> = styled(Tab)<StyledTabProps>(({
  theme,
  size = 'medium',
}) => {
  const config = sizeConfigs[size];

  return {
    minHeight: config.height - 2, // 40px for medium (42 - 2 for borders)
    height: config.height - 2,
    fontSize: config.fontSize,
    fontWeight: 500,
    textTransform: 'none',
    padding: config.padding,
    minWidth: 0,
    borderRadius: config.borderRadius,
    color: 'rgba(83, 89, 118, 0.7)', // Lighter color for unselected
    transition: 'color 0.3s ease, transform 0.2s ease',
    position: 'relative',
    zIndex: 2,
    opacity: 1,
    cursor: 'pointer',
    lineHeight: 'normal',

    // Icon styling
    '& .MuiTab-iconWrapper': {
      marginBottom: 0,
      marginRight: theme.spacing(0.5),
    },

    // When selected
    '&.Mui-selected': {
      color: '#535976', // Darker color for selected, matching Today button
      fontWeight: 500,
      cursor: 'default',
    },

    // Hover effect for inactive tabs
    '&:hover:not(.Mui-selected)': {
      backgroundColor: 'transparent',
      color: 'rgba(83, 89, 118, 0.9)', // Darken on hover
    },

    // No effect on selected tab hover
    '&.Mui-selected:hover': {
      backgroundColor: 'transparent',
      transform: 'none',
    },

    '& .MuiTouchRipple-root': {
      display: 'none',
    },

    // Disabled state
    '&.Mui-disabled': {
      opacity: 0.5,
    },

    // Focus visible state for accessibility
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
      outline: '2px solid',
      outlineColor: theme.palette.primary.main,
      outlineOffset: -2,
    },
  };
});
